/**
 * Products Tab Component
 * Shows product performance metrics, best sellers, slow movers, and export functionality
 */
import { TrendingUp, TrendingDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ProductsTab({ 
  productPerformance,
  onExportProducts,
  formatCurrency,
  formatPercent
}) {
  if (!productPerformance) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        No product data available
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-tab">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Total Revenue</p>
          <p className="text-2xl font-bold text-foreground">
            {formatCurrency(productPerformance.summary?.total_revenue)}
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Units Sold</p>
          <p className="text-2xl font-bold text-foreground">
            {productPerformance.summary?.total_units_sold || 0}
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Total Profit</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {formatCurrency(productPerformance.summary?.total_profit)}
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Avg Margin</p>
          <p className="text-2xl font-bold text-foreground">
            {formatPercent(productPerformance.summary?.avg_margin)}
          </p>
        </div>
      </div>

      {/* Most Ordered & Slow Movers */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Most Ordered */}
        <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Most Ordered
          </h3>
          <div className="space-y-3">
            {productPerformance.best_sellers?.map((item, i) => (
              <div key={item.item_id} className="flex items-center gap-3">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  i === 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                  i === 1 ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {i + 1}
                </span>
                {item.image ? (
                  <img src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-muted shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.units_sold} sold</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-medium text-foreground">{formatCurrency(item.revenue)}</p>
                  <p className="text-xs text-green-600 dark:text-green-400">{formatPercent(item.margin)} margin</p>
                </div>
              </div>
            ))}
            {!productPerformance.best_sellers?.length && (
              <p className="text-center text-muted-foreground py-4">No products sold yet</p>
            )}
          </div>
        </div>

        {/* Slow Movers */}
        <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-orange-500" />
            Slow Movers
          </h3>
          <div className="space-y-3">
            {productPerformance.slow_movers?.slice(0, 8).map((item) => (
              <div key={item.item_id} className="flex items-center gap-3">
                {item.image ? (
                  <img src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-muted shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{item.category}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                    {item.units_sold} sold
                  </p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(item.price)}</p>
                </div>
              </div>
            ))}
            {!productPerformance.slow_movers?.length && (
              <p className="text-center text-muted-foreground py-4">No slow movers detected</p>
            )}
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={onExportProducts} className="gap-2">
          <Download className="w-4 h-4" />
          Export Product Report
        </Button>
      </div>
    </div>
  );
}
